/** Dev-only preview harness for the signed-out / login surface.
 *
 * WHY: the studio talks to the backend same-origin (`/api`, proxied to
 * 127.0.0.1:8000 in dev). With no backend running, `accounts()` — the first
 * thing the auth gate calls — fails, resolves to `null`, and the app drops into
 * single-user "local" mode, mounting the full shell whose queries then flood
 * the dead proxy with errors. That means the login screen can't be previewed
 * without standing up Python + a configured Supabase/Firebase project.
 *
 * This module lets you preview the login UI with ZERO network: open the studio
 * with `?preview` (dev only). `useAuthGate` then uses `mockAccounts()` instead
 * of the real provider, so the signed-out screen renders and every auth action
 * is simulated in-memory. Because the signed-out branch never mounts the shell,
 * NO `/api` request is ever made — no proxy errors.
 *
 * This is strictly a dev aid: `isPreview()` is hard-gated on `import.meta.env.DEV`
 * and an explicit `?preview` flag, and the mock is loaded lazily so it never
 * enters the production path. It does NOT change the real auth contract
 * (docs/architecture/frontend/app-shell-and-bundles.md).
 */

import type { AccountUser, Accounts, SignUpResult } from "@/lib/accounts";

/** True only in a dev build AND when the URL carries `?preview`. */
export function isPreview(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return new URLSearchParams(window.location.search).has("preview");
  } catch {
    return false;
  }
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const fakeUser = (email: string): AccountUser => ({
  uid: "preview-user",
  email,
  displayName: email.split("@")[0] ?? "Preview User",
  photoURL: null,
});

/** In-memory auth state so `onUser` reflects mock sign-in / sign-out. */
let currentUser: AccountUser | null = null;
const listeners = new Set<(u: AccountUser | null) => void>();
function emit() {
  for (const l of listeners) l(currentUser);
}

let cached: Accounts | null = null;

/** A fully in-memory `Accounts` (Supabase-shaped) for previewing the login.
 *
 * Special addresses let you exercise every state without a backend:
 *   • anything with "fail"  → the call REJECTS (drives the failure Alert)
 *   • anything with "hang"  → the call NEVER settles (drives the login timeout)
 *   • anything with "verify"→ sign-up returns needsVerification (email panel)
 *   • any other address     → succeeds after a short delay
 */
export function mockAccounts(): Accounts {
  if (cached) return cached;

  cached = {
    provider: "supabase",
    signInWithGoogle: async () => {
      await delay(900);
      currentUser = { ...fakeUser("you@gmail.com"), displayName: "Preview User" };
      emit();
      return currentUser;
    },
    signInWithPassword: async (email, _password) => {
      void _password;
      if (email.includes("hang")) return new Promise<AccountUser>(() => {}); // never settles
      await delay(700);
      if (email.includes("fail")) throw new Error("Invalid login credentials. (preview)");
      currentUser = fakeUser(email);
      emit();
      return currentUser;
    },
    signUp: async (email, _password): Promise<SignUpResult> => {
      void _password;
      if (email.includes("hang")) return new Promise<SignUpResult>(() => {});
      await delay(700);
      if (email.includes("fail")) throw new Error("That email is already registered. (preview)");
      if (email.includes("verify")) return { user: fakeUser(email), needsVerification: true };
      currentUser = fakeUser(email);
      emit();
      return { user: currentUser, needsVerification: false };
    },
    resendVerification: async () => {
      await delay(500);
    },
    resetPassword: async (email) => {
      await delay(600);
      if (email.includes("fail")) throw new Error("No account found for that email. (preview)");
    },
    signOut: async () => {
      await delay(200);
      currentUser = null;
      emit();
    },
    onUser: (cb) => {
      listeners.add(cb);
      cb(currentUser);
      return () => listeners.delete(cb);
    },
  };
  return cached;
}
